import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { auth, googleProvider } from "../services/firebase";
import { User, AuthContextType } from "../types";
import { api } from "../services/api";
import log from "../utils/logger";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [requiresRegistration, setRequiresRegistration] = useState(false);

  const mapFirebaseUser = (firebaseUser: FirebaseUser | null): User | null => {
    if (!firebaseUser) return null;
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
    };
  };

  const checkUserAccess = async () => {
    try {
      // Force token refresh to get latest custom claims
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          await currentUser.getIdToken(true); // true forces refresh
          log.debug("checkUserAccess - Token refreshed");
        } catch (tokenError) {
          log.warn("Failed to refresh token:", tokenError);
        }
      }

      const response = await api.authUser();
      log.debug("checkUserAccess - API response:", response);

      // Handle user data regardless of success status (for rejected/pending status display)
      if (response.user) {
        log.debug("checkUserAccess - User role:", response.user.role);
        log.debug("checkUserAccess - User status:", response.user.status);
        // Merge user data from API with Firebase Auth data
        setUser((prev) => {
          const updatedUser: User = {
            ...prev,
            uid: response.user!.uid,
            email: response.user!.email,
            displayName: response.user!.displayName || prev?.displayName || null,
            photoURL: response.user!.photoURL || prev?.photoURL || null,
            role: response.user!.role,
            status: response.user!.status,
            requestedRole: response.user!.requestedRole,
          };
          log.debug("checkUserAccess - Updated user state:", updatedUser);
          return updatedUser;
        });

        // Handle status-based registration requirements
        if (response.user.status === "rejected") {
          // Rejected users don't need registration
          setRequiresRegistration(false);
        } else if (response.user.status === "pending") {
          // If user is pending and has a requestedRole, they've already submitted
          if (response.user.requestedRole) {
            setRequiresRegistration(false);
          } else {
            setRequiresRegistration(true);
          }
        } else if (response.requiresRegistration) {
          setRequiresRegistration(true);
        } else {
          setRequiresRegistration(false);
        }
      } else {
        // No user data returned - likely first time user
        setRequiresRegistration(true);
      }
    } catch (error) {
      log.error("Error checking user access:", error);
      // On error, still allow navigation but set requires registration
      setRequiresRegistration(true);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        const mappedUser = mapFirebaseUser(firebaseUser);
        setUser(mappedUser);
        // Check user access after login - wait for it to complete
        await checkUserAccess();
      } else {
        setUser(null);
        setRequiresRegistration(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      // checkUserAccess will be called automatically via onAuthStateChanged
    } catch (error) {
      log.error("Error signing in with Google:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setRequiresRegistration(false);
    } catch (error) {
      log.error("Error signing out:", error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    checkUserAccess,
    requiresRegistration,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

